import { createMotionTransition } from './interaction';
import type { CSSObject } from '@mui/material/styles';
import { designTokens } from './tokens';

export type InputSurfaceOptions = {
  /** 실제 테두리, 안쪽 테두리, MUI OutlinedInput의 notch 중 하나를 사용합니다. */
  variant?: 'outlined' | 'inset' | 'notched';
  disabled?: boolean;
  invalid?: boolean;
  /** 기본은 스크롤 경계에서도 잘리지 않는 안쪽 초점입니다. */
  focusIndicator?: 'outer' | 'inset';
};

/**
 * 입력을 감싸는 요소의 상태 표현입니다. 크기·배경·입력 동작·ARIA는 소비자가 소유합니다.
 * 읽기 전용도 초점을 받을 수 있으므로 readOnly를 disabled로 전달하지 않습니다.
 */
export function createInputSurfaceStyles({
  variant = 'outlined',
  disabled = false,
  invalid = false,
  focusIndicator = 'inset',
}: InputSurfaceOptions = {}): CSSObject {
  const outlineSelector = '& .MuiOutlinedInput-notchedOutline';
  const focusSelector = '&.Mui-focused:not(.Mui-disabled), &:focus-within:not(.Mui-disabled)';
  const borderColor = disabled ? designTokens.color.divider : invalid ? designTokens.color.danger : designTokens.color.divider;

  const frame = (color: string, focused = false): CSSObject => {
    const insetFocused = focused && focusIndicator === 'inset';
    const width = !disabled && (invalid || insetFocused) ? 2 : 1;
    const outerRing = focused && !insetFocused
      ? invalid ? designTokens.shadow.inputErrorFocus : designTokens.shadow.inputFocus
      : undefined;
    // 오류 경계와 초점은 별개입니다. Canvas의 inverse 간격+brand ring을 안쪽으로 옮겨
    // 실제 스크롤 viewport에서도 두 상태가 잘리지 않게 유지합니다.
    const invalidFocus = insetFocused && invalid;
    const insetRing = invalidFocus
      ? `inset 0 0 0 ${width}px ${color}, inset 0 0 0 ${width + 2}px ${designTokens.color.canvas}, inset 0 0 0 ${width + 4}px ${designTokens.color.action}`
      : `inset 0 0 0 ${width}px ${color}`;
    if (variant === 'inset') {
      return { boxShadow: `${insetRing}${outerRing ? `, ${outerRing}` : ''}` };
    }
    if (variant === 'notched') {
      return {
        // 별도 fieldset은 root의 inset shadow와 겹치므로 경계 자체를 2px로 표시합니다.
        [outlineSelector]: { borderColor: color, borderWidth: width },
        boxShadow: invalidFocus ? insetRing : outerRing ?? 'none',
      };
    }
    const insideBorder = invalidFocus
      ? `inset 0 0 0 1px ${color}, inset 0 0 0 3px ${designTokens.color.canvas}, inset 0 0 0 5px ${designTokens.color.action}`
      : width > 1 ? `inset 0 0 0 1px ${color}` : undefined;
    return { border: `1px solid ${color}`, boxShadow: [insideBorder, outerRing].filter(Boolean).join(', ') || 'none' };
  };

  return {
    borderRadius: designTokens.radius.input,
    transition: createMotionTransition(['border-color', 'box-shadow', 'background-color'], 'input'),
    ...frame(borderColor),
    ...(variant === 'notched' ? {
      [outlineSelector]: { borderColor, borderWidth: invalid && !disabled ? 2 : 1, transition: createMotionTransition('border-color', 'input') },
      // MUI의 기본 error·disabled 선택자보다 공용 상태 계약을 우선합니다.
      '&.Mui-error, &.Mui-disabled': frame(borderColor),
    } : {}),
    ...(!disabled ? {
      '&:hover:not(.Mui-disabled):not(.Mui-focused):not(:focus-within)': frame(
        invalid ? designTokens.color.danger : designTokens.color.strongDivider,
      ),
      [focusSelector]: frame(
        invalid ? designTokens.color.danger : designTokens.color.action,
        true,
      ),
    } : {}),
    '@media (prefers-reduced-motion: reduce)': {
      transition: 'none',
      ...(variant === 'notched' ? { [outlineSelector]: { transition: 'none' } } : {}),
    },
    '@media (forced-colors: active)': {
      // 강제 색상에서는 box-shadow가 사라지므로 안쪽 테두리도 outline으로 보완합니다.
      ...(variant === 'inset' ? { outline: `1px solid ${disabled ? 'GrayText' : 'CanvasText'}`, outlineOffset: -1 } : {}),
      ...(!disabled ? { [focusSelector]: { outline: '2px solid Highlight', outlineOffset: focusIndicator === 'inset' ? -4 : 2 } } : {}),
    },
  };
}
